//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles, Grid, Container, Typography, Divider } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';

// import AfitdTypography from '../../common/elemental/AfitdTypography';
// import AfitdTextField from '../../common/elemental/AfitdTextField';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { HOMEPAGE_CONTACT, BASE_URL, PRICE_ADVERTISEMENT_PLACEMENT } from '../../common/constants';
import { PATH_ADVERTISE, PATH_HOME } from '../../system/constants';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import Button from '../../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';
import bannerSrc from '../../images/advertise/banner.jpg';
import stepsSrc from '../../images/advertise/steps.png';
import card1Src from '../../images/advertise/card1.jpg';
import card2Src from '../../images/advertise/card2.jpg';
import card3Src from '../../images/advertise/card3.jpg';
import cardMd1 from '../../images/advertise/cardMd1.jpg';
import cardMd2 from '../../images/advertise/cardMd2.jpg';
import cardMd3 from '../../images/advertise/cardMd3.jpg';
import pricingSrc from '../../images/advertise/pricing.png';
import pricingSmallSrc from '../../images/advertise/pricingSmall.png';
import stepsSmallSrc from '../../images/advertise/step-small.png';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {
        backgroundColor: 'white',
    },
    exampleWithNoPropsOnlyStyles: { color: 'red', marginTop: 30, marginBottom: 40 },
    nonReactElementExample: { color: 'blue', marginTop: 50, cursor: 'pointer' },
    banner: {
        backgroundImage: `url(${bannerSrc})`,
        width: '100%',
        height: '540px',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        textAlign: 'center',
        [theme.breakpoints.down('md')]: { height: '350px' },
        [theme.breakpoints.down('md')]: { height: '320px' },
    },
    bannerHeading: {
        fontWeight: 'bold',
        fontSize: '54px',
        lineHeight: '68px',
        color: 'white',
        paddingTop: '225px',
        [theme.breakpoints.down('md')]: { fontSize: '40px', lineHeight: '58px', paddingTop: '140px' },
    },
    bannerBottomHeading: {
        margin: 'auto',
        fontWeight: 'bold',
        fontSize: '42px',
        lineHeight: '59px',
        marginTop: '80px',
        marginBottom: '74px',
        textAlign: 'center',
        [theme.breakpoints.down('md')]: { fontSize: '38px', marginTop: '64px', marginBottom: '42px' },
        [theme.breakpoints.down('sm')]: { fontSize: '28px', marginTop: '46px', marginBottom: '40px' },
    },
    contactUsBottomHeading: {
        margin: 'auto',
        fontWeight: 'bold',
        fontSize: '42px',
        lineHeight: '59px',
        marginTop: '80px',
        marginBottom: '74px',
        textAlign: 'center',
        [theme.breakpoints.down('md')]: { fontSize: '38px', marginTop: '64px', marginBottom: '42px' },
        [theme.breakpoints.down('sm')]: { fontSize: '28px', marginTop: '46px', marginBottom: '25px' },
    },
    stepsBody: {
        textAlign: 'center',
        marginTop: '40px',
    },
    card1: {
        backgroundImage: `url(${card1Src})`,
        width: '100%',
        height: '550px',
        borderRadius: '8px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'margin 400ms',
        [theme.breakpoints.down('md')]: { textAlign: 'start', backgroundImage: `url(${cardMd1})`, backgroundSize: 'cover', height: 'auto' },
        [theme.breakpoints.down('sm')]: { backgroundImage: `url(${card1Src})` },
    },
    card2: {
        backgroundImage: `url(${card2Src})`,
        width: '100%',
        height: '550px',
        borderRadius: '8px',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'margin 400ms',
        [theme.breakpoints.down('md')]: { textAlign: 'start', backgroundImage: `url(${cardMd2})`, backgroundSize: 'cover', height: 'auto' },
        [theme.breakpoints.down('sm')]: { backgroundImage: `url(${card2Src})` },
    },
    card3: {
        cursor: 'pointer',
        backgroundImage: `url(${card3Src})`,
        width: '100%',
        height: '550px',
        borderRadius: '8px',
        textAlign: 'center',
        transition: 'margin 400ms',
        [theme.breakpoints.down('md')]: { textAlign: 'start', backgroundImage: `url(${cardMd3})`, backgroundSize: 'cover', height: 'auto' },
        [theme.breakpoints.down('sm')]: { backgroundImage: `url(${card3Src})` },
    },
    overlay: {},
    cardTitle: {
        fontWeight: '900',
        fontSize: '50px',
        lineHeight: '60px',
        color: 'white',
        paddingTop: '225px',
        pointerEvents: 'none',
    },
    cardDetails: {
        padding: '24px',
        pointerEvents: 'none',
        paddingTop: '40px',
        transition: 'display 1s',
    },
    pricing: {
        width: '100%',
        marginLeft: '1px',
        marginTop: '100px',
    },
    buttonBody: {
        textAlign: 'center',
        paddingTop: '30px',
    },
    bottomText: {
        fontSize: '20px',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: { fontSize: 18 },
    },
    contactLink: {
        fontWeight: 'bold',
        textDecoration: 'underline',
        color: '#1D8A8B',
        cursor: 'pointer',
    },
    detailsHeading: {
        margin: '0px',
        color: 'white',
        fontSize: '36px',
        fontWeight: '900',
        [theme.breakpoints.down('sm')]: { fontSize: 24 },
    },
    detailText: {
        fontSize: '20px',
        lineHeight: '30px',
        color: 'white',
    },
    haveQuestion: {
        marginTop: '100px',
    },
    steps: { width: '100%' },
    cardGrid: { padding: '4px', [theme.breakpoints.down('md')]: { padding: '12px 0px' } },
    button: { [theme.breakpoints.down('sm')]: { width: '100%' } },
    simpleProcessText: {
        textTransform: 'uppercase',
        fontWeight: 'bolder',
    },
    pricingAndProcessBody: {
        backgroundColor: '#111e14',
        borderRadius: '8px',
        padding: '30px',
        paddingTop: '40px',
        paddingBottom: '40px',
        marginTop: '10px',
        [theme.breakpoints.down('sm')]: {
            padding: '15px',
        },
    },
    simpleHeadings: {
        fontWeight: 'bolder',
        color: 'white',
        fontSize: '4rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '2.5rem',
        },
    },
    simpleDescription: {
        color: 'white',
        marginTop: '20px',
        [theme.breakpoints.down('sm')]: {
            textAlign: 'left',
        },
    },
    simpleDescriptionDiv: {
        color: 'white',
        marginTop: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        },
    },
    checkIcon: {
        backgroundColor: 'white',
        color: '#1D8A8B',
        borderRadius: '100%',
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px',
        },
    },
    adItem: {
        display: 'flex',
        alignItems: 'center',
    },
    adText: {
        color: 'white',
        paddingLeft: '5px',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, handleSelectCard, selectedCard, history, smMatches } = params;

    const defns = {
        helloText: { variant: 'h4' },
        textFieldExample: { value: 'Hello TextField' },
        nonReactElementExample: {
            onClick: evt => {
                alert('Clicked!');
            },
        },
        steps: {
            src: smMatches ? stepsSmallSrc : stepsSrc,
        },
        pricing: {
            src: smMatches ? pricingSmallSrc : pricingSrc,
        },
        card1: {
            onMouseOver: () => handleSelectCard('card1'),
            onMouseOut: () => handleSelectCard('card1'),
            style: {
                boxShadow:
                    selectedCard === 'card1' &&
                    'rgba(0, 0, 0, 0.09) 0px 2px 1px, rgba(0, 0, 0, 0.09) 0px 4px 2px, rgba(0, 0, 0, 0.09) 0px 8px 4px, rgba(0, 0, 0, 0.09) 0px 16px 8px, rgba(0, 0, 0, 0.09) 0px 32px 16px',
                marginTop: selectedCard === 'card1' && '-7px',
            },
        },
        card2: {
            onMouseOver: () => handleSelectCard('card2'),
            onMouseOut: () => handleSelectCard('card2'),
            style: {
                boxShadow:
                    selectedCard === 'card2' &&
                    'rgba(0, 0, 0, 0.09) 0px 2px 1px, rgba(0, 0, 0, 0.09) 0px 4px 2px, rgba(0, 0, 0, 0.09) 0px 8px 4px, rgba(0, 0, 0, 0.09) 0px 16px 8px, rgba(0, 0, 0, 0.09) 0px 32px 16px',
                marginTop: selectedCard === 'card2' && '-7px',
            },
        },
        card3: {
            onMouseOver: () => handleSelectCard('card3'),
            onMouseOut: () => handleSelectCard('card3'),
            style: {
                boxShadow:
                    selectedCard === 'card3' &&
                    'rgba(0, 0, 0, 0.09) 0px 2px 1px, rgba(0, 0, 0, 0.09) 0px 4px 2px, rgba(0, 0, 0, 0.09) 0px 8px 4px, rgba(0, 0, 0, 0.09) 0px 16px 8px, rgba(0, 0, 0, 0.09) 0px 32px 16px',
                marginTop: selectedCard === 'card3' && '-7px',
            },
        },
        cardContainer: { container: true },
        cardGrid: { item: true, xs: 12, lg: 4 },
        button: {
            color: 'primary',
            onClick: () => {
                document.getElementsByClassName('App')[0].firstElementChild.scrollTo(0, 0);
                history.push(`${BASE_URL}${PATH_ADVERTISE}/start`);
            },
        },
        contactLink: {
            onClick: () => history.push(`${BASE_URL}${PATH_HOME}/${HOMEPAGE_CONTACT}`),
        },
        simpleProcessText: {
            color: 'secondary',
            variant: 'subtitle1',
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const IntroPage = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));
    const mdMatchesOnly = useMediaQuery(theme.breakpoints.only('md'));
    const lgMatchesUp = useMediaQuery(theme.breakpoints.up('lg'));
    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { scrollToPageTop } = layoutContext;
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [isDirty, setIsDirty] = useState(false);
    const [selectedCard, setSelectedCard] = React.useState('');

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // on mount, scroll to the top of the page
    useEffect(() => {
        scrollToPageTop();
    }, [scrollToPageTop]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleSelectCard = card => {
        if (selectedCard === card) {
            setSelectedCard('');
        } else {
            setSelectedCard(card);
        }
    };
    // console.log(selectedCard);

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, handleSelectCard, selectedCard, history, smMatches });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.banner}>
                <div {...defns.bannerHeading}>OPPORTUNITY IS KNOCKING...</div>
            </div>
            <Container>
                <div {...defns.bannerBottomHeading}>
                    Boost visibility and engagement {mdMatchesOnly && <br />} YOUR way
                    {lgMatchesUp && <br />} with our custom {mdMatchesOnly && <br />} advertising solution.
                </div>

                <div {...defns.stepsBody}>
                    {/* <Stepper /> */}
                    <img alt="" {...defns.steps} />
                </div>

                <br />
                <br />
                <div>
                    <div {...defns.bannerBottomHeading}>Choose Your Audience</div>
                    <Grid {...defns.cardContainer}>
                        <Grid {...defns.cardGrid}>
                            <div {...defns.card1}>
                                {selectedCard === 'card1' ? (
                                    <div {...defns.cardDetails}>
                                        <h2 {...defns.detailsHeading}>JOB {lgMatchesUp && <br />} CENTER</h2>
                                        <p {...defns.detailText}>The Job Center page is the hub where job seekers will be searching for open opportunities and job listings.</p>
                                        <p {...defns.detailText}>
                                            By choosing this page for your advertisement you can expect to engage with talent applicants with 0-5 years of work experience.{' '}
                                        </p>
                                    </div>
                                ) : (
                                    <div {...defns.cardTitle}>
                                        JOB <br /> CENTER
                                    </div>
                                )}
                            </div>
                        </Grid>
                        <Grid {...defns.cardGrid}>
                            <div {...defns.card2}>
                                {selectedCard === 'card2' ? (
                                    <div {...defns.cardDetails}>
                                        <h2 {...defns.detailsHeading}>
                                            EMPLOYER {lgMatchesUp && <br />}
                                            SEARCH
                                        </h2>
                                        <p {...defns.detailText}>This page is where job seekers can research companies that may be a potential employer.</p>
                                        <p {...defns.detailText}>
                                            By choosing this page for your advertisement you can expect to receive visibility for your product and/or service from a wide-range of
                                            individuals.
                                        </p>
                                    </div>
                                ) : (
                                    <div {...defns.cardTitle}>
                                        EMPLOYER <br />
                                        SEARCH
                                    </div>
                                )}
                            </div>
                        </Grid>
                        <Grid {...defns.cardGrid}>
                            <div {...defns.card3}>
                                {selectedCard === 'card3' ? (
                                    <div {...defns.cardDetails}>
                                        <h2 {...defns.detailsHeading}>TALENT {lgMatchesUp && <br />} CENTER</h2>
                                        <p {...defns.detailText}>
                                            The Talent Center page is where employers can search for new candidates for current openings within their company.
                                        </p>
                                        <p {...defns.detailText}>
                                            By choosing this page for your advertisement you can expect to engage with a diverse range of companies from small business to large
                                            corporations.
                                        </p>
                                    </div>
                                ) : (
                                    <div {...defns.cardTitle}>
                                        TALENT <br /> CENTER
                                    </div>
                                )}
                            </div>
                        </Grid>
                    </Grid>
                </div>
                <div {...defns.stepsBody}>
                    <br />
                    <br />
                    <div>
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item xs={6}>
                                <Typography {...defns.simpleProcessText}>Simple Pricing.</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography {...defns.simpleProcessText}>Simple Process.</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container alignItems="center" justifyContent="space-between" {...defns.pricingAndProcessBody}>
                                    <Grid item xs={5}>
                                        <Typography variant="h2" {...defns.simpleHeadings}>
                                            ${PRICE_ADVERTISEMENT_PLACEMENT} USD
                                        </Typography>
                                        <div {...defns.simpleDescriptionDiv}>
                                            <div {...defns.adItem}>
                                                <CheckCircleIcon {...defns.checkIcon} />{' '}
                                                <Typography variant="subtitle1" {...defns.adText}>
                                                    1 Placement
                                                </Typography>
                                            </div>
                                            <div {...defns.adItem}>
                                                <CheckCircleIcon {...defns.checkIcon} />{' '}
                                                <Typography variant="subtitle1" {...defns.adText}>
                                                    1 Page
                                                </Typography>
                                            </div>
                                            <div {...defns.adItem}>
                                                <CheckCircleIcon {...defns.checkIcon} />{' '}
                                                <Typography variant="subtitle1" {...defns.adText}>
                                                    30 Minutes
                                                </Typography>
                                            </div>
                                        </div>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Divider orientation="vertical" flexItem style={{ background: 'lightgrey', height: '150px', margin: 'auto' }} />
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant="h2" {...defns.simpleHeadings}>
                                            6 Steps
                                        </Typography>
                                        <Typography variant="subtitle1" {...defns.simpleDescription}>
                                            Powerful Brand Exposure in Minutes
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </div>
                    {/* <img alt="" {...defns.pricing} /> */}
                </div>
                <div {...defns.buttonBody}>
                    <Button {...defns.button}>BEGIN AD PROCESS</Button>
                </div>
                <div {...defns.haveQuestion}>
                    <div {...defns.contactUsBottomHeading}>Have other questions?</div>
                    <div {...defns.bottomText}>
                        <span {...defns.contactLink}>Contact Us</span>
                        <span> here so we can help</span>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default IntroPage;
